import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { BsArrowLeft, BsCheckCircle, BsXCircle, BsFileEarmarkText, BsCalendar, BsBriefcase, BsEnvelope, BsX } from 'react-icons/bs';
import dispatchService, { ReceivedApplication } from '../services/dispatchService';

const ApplicationsDashboard: React.FC = () => {
    const navigate = useNavigate();
    const [applications, setApplications] = useState<ReceivedApplication[]>([]);
    const [loading, setLoading] = useState(true);
    const [viewingApp, setViewingApp] = useState<ReceivedApplication | null>(null);
    const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);

    useEffect(() => {
        loadApplications();
    }, []);

    const loadApplications = async () => {
        try {
            setLoading(true);
            const response = await dispatchService.getReceivedApplications();
            if (response.success) {
                setApplications(response.applications);
            }
        } catch (error) {
            console.error('Failed to load applications:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleStatusUpdate = async (taskId: string, appId: string, status: 'accepted' | 'rejected') => {
        try {
            await dispatchService.adjudicateApplication(taskId, appId, status);
            // Refresh list
            loadApplications();
            if (viewingApp && viewingApp._id === appId) {
                setViewingApp(prev => prev ? { ...prev, status } : null);
            }
        } catch (error) {
            console.error('Failed to update status:', error);
        }
    };

    const getTimeElapsed = (dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();

        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        return `${diffDays}d ago`;
    };

    return (
        <div className="min-h-screen bg-premium-wood-dark font-sans text-white pt-24 pb-12 px-4 sm:px-6">
            <div className="max-w-6xl mx-auto">
                <div className="flex items-center gap-4 mb-8">
                    <button
                        onClick={() => navigate('/dispatch')}
                        className="p-2 rounded-full hover:bg-white/5 transition-colors"
                    >
                        <BsArrowLeft className="w-6 h-6 text-premium-gold" />
                    </button>
                    <div>
                        <h1 className="text-3xl font-serif font-bold text-transparent bg-clip-text bg-gradient-to-r from-premium-gold-light to-premium-gold">
                            Received Applications
                        </h1>
                        <p className="text-white/50 text-sm mt-1">
                            Manage candidates for your posted jobs
                        </p>
                    </div>
                </div>

                {loading ? (
                    <div className="flex justify-center py-20">
                        <div className="w-10 h-10 border-4 border-premium-gold border-t-transparent rounded-full animate-spin"></div>
                    </div>
                ) : applications.length === 0 ? (
                    <div className="text-center py-20 bg-white/5 rounded-2xl border border-white/10">
                        <BsBriefcase className="w-16 h-16 text-white/20 mx-auto mb-4" />
                        <h3 className="text-xl font-bold text-white mb-2">No Applications Yet</h3>
                        <p className="text-white/50">When candidates apply to your jobs, they will appear here.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* List Column */}
                        <div className="lg:col-span-1 space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto custom-scrollbar pr-2">
                            {applications.map(app => (
                                <motion.div
                                    key={app._id}
                                    layoutId={app._id}
                                    onClick={() => setViewingApp(app)}
                                    className={`p-4 rounded-xl border cursor-pointer transition-all ${viewingApp?._id === app._id
                                        ? 'bg-gradient-to-br from-premium-gold/20 to-premium-gold/5 border-premium-gold'
                                        : 'bg-white/5 border-white/10 hover:bg-white/10'
                                        }`}
                                >
                                    <div className="flex justify-between items-start mb-2">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-premium-gold/20 flex items-center justify-center text-premium-gold font-bold overflow-hidden border border-premium-gold/30">
                                                {app.applicant.avatar ? (
                                                    <img
                                                        src={app.applicant.avatar}
                                                        alt=""
                                                        className="w-full h-full object-cover"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setFullscreenImage(app.applicant.avatar || null);
                                                        }}
                                                    />
                                                ) : (
                                                    <span>{app.applicant.firstName[0]}</span>
                                                )}
                                            </div>
                                            <div>
                                                <h3 className="font-bold text-sm text-white">{app.applicant.firstName} {app.applicant.lastName}</h3>
                                                <p className="text-xs text-white/50 truncate max-w-[150px]">{app.task.title}</p>
                                            </div>
                                        </div>
                                        <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-bold ${app.status === 'accepted' ? 'bg-green-500/20 text-green-400' :
                                            app.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                                                'bg-premium-gold/20 text-premium-gold'
                                            }`}>
                                            {app.status}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-end mt-2">
                                        <div className="flex items-center gap-1 text-[10px] text-white/40">
                                            <BsCalendar className="w-3 h-3" />
                                            {getTimeElapsed(app.appliedAt)}
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>

                        {/* Detail Column */}
                        <div className="lg:col-span-2">
                            <AnimatePresence mode="wait">
                                {viewingApp ? (
                                    <motion.div
                                        key={viewingApp._id}
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -20 }}
                                        className="bg-black/30 backdrop-blur-md border border-premium-gold/20 rounded-2xl p-6 md:p-8 sticky top-24"
                                    >
                                        <div className="flex flex-col md:flex-row justify-between items-start gap-6 mb-8 pb-8 border-b border-white/10">
                                            <div className="flex items-center gap-4">
                                                <div
                                                    className="w-20 h-20 rounded-full bg-premium-gold/20 flex items-center justify-center text-premium-gold text-2xl font-bold overflow-hidden shadow-[0_0_20px_rgba(212,175,55,0.2)] cursor-zoom-in group relative border border-premium-gold/30"
                                                    onClick={() => setFullscreenImage(viewingApp.applicant.avatar || null)}
                                                >
                                                    {viewingApp.applicant.avatar ? (
                                                        <img src={viewingApp.applicant.avatar} alt="" className="w-full h-full object-cover transition-transform group-hover:scale-110" />
                                                    ) : (
                                                        <span>{viewingApp.applicant.firstName[0]}</span>
                                                    )}
                                                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                        <span className="text-[10px] text-white font-bold">VIEW</span>
                                                    </div>
                                                </div>
                                                <div>
                                                    <h2 className="text-2xl font-serif font-bold text-white mb-1">
                                                        {viewingApp.applicant.firstName} {viewingApp.applicant.lastName}
                                                    </h2>
                                                    <div className="flex flex-wrap gap-2 text-sm text-white/60">
                                                        {viewingApp.applicant.title && <span>{viewingApp.applicant.title}</span>}
                                                        {viewingApp.applicant.location && (
                                                            <>
                                                                <span>•</span>
                                                                <span>{viewingApp.applicant.location}</span>
                                                            </>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-2 mt-2 text-sm text-premium-gold/80">
                                                        <BsEnvelope />
                                                        <a href={`mailto:${viewingApp.applicant.email}`} className="hover:underline">
                                                            {viewingApp.applicant.email}
                                                        </a>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex flex-col items-end gap-2">
                                                <div className="text-right">
                                                    <p className="text-xs text-white/40 mb-1">Applying for</p>
                                                    <h3 className="font-bold text-premium-gold">{viewingApp.task.title}</h3>
                                                </div>
                                                <div className="text-right mt-2 text-xs text-white/40">
                                                    Sent {new Date(viewingApp.appliedAt).toLocaleString()}
                                                    <br />
                                                    ({getTimeElapsed(viewingApp.appliedAt)})
                                                </div>
                                            </div>
                                        </div>

                                        <div className="space-y-6">
                                            <div>
                                                <h3 className="text-lg font-bold text-white mb-3">Cover Letter / Message</h3>
                                                <div className="bg-white/5 p-4 rounded-xl text-white/80 leading-relaxed whitespace-pre-wrap border border-white/5">
                                                    {viewingApp.message}
                                                </div>
                                            </div>

                                            {viewingApp.cvUrl && (
                                                <div>
                                                    <h3 className="text-lg font-bold text-white mb-3">Attachments</h3>
                                                    <a
                                                        href={viewingApp.cvUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="inline-flex items-center gap-3 px-4 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl transition-colors text-premium-gold"
                                                    >
                                                        <BsFileEarmarkText className="w-5 h-5" />
                                                        <span>View CV / Portfolio</span>
                                                    </a>
                                                </div>
                                            )}

                                            {viewingApp.applicant.skills && viewingApp.applicant.skills.length > 0 && (
                                                <div>
                                                    <h3 className="text-lg font-bold text-white mb-3">Skills</h3>
                                                    <div className="flex flex-wrap gap-2">
                                                        {viewingApp.applicant.skills.map((skill: string, idx: number) => (
                                                            <span key={idx} className="px-3 py-1 bg-white/5 rounded-full text-xs border border-white/10 text-white/70">
                                                                {skill}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {viewingApp.status === 'pending' && (
                                            <div className="mt-12 pt-8 border-t border-white/10 flex flex-col sm:flex-row gap-4 justify-end">
                                                <button
                                                    onClick={() => handleStatusUpdate(viewingApp.task._id, viewingApp._id, 'rejected')}
                                                    className="px-6 py-3 border border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-xl font-bold transition-all flex items-center justify-center gap-2"
                                                >
                                                    <BsXCircle /> Reject Application
                                                </button>
                                                <button
                                                    onClick={() => handleStatusUpdate(viewingApp.task._id, viewingApp._id, 'accepted')}
                                                    className="px-6 py-3 bg-gradient-to-r from-premium-gold-dark to-premium-gold-light text-premium-wood-dark rounded-xl font-bold hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] transition-all flex items-center justify-center gap-2"
                                                >
                                                    <BsCheckCircle /> Accept Candidate
                                                </button>
                                            </div>
                                        )}

                                        {viewingApp.status !== 'pending' && (
                                            <div className={`mt-12 pt-8 border-t border-white/10 text-center p-4 rounded-xl ${viewingApp.status === 'accepted' ? 'bg-green-500/10 border border-green-500/20 text-green-400' : 'bg-red-500/10 border border-red-500/20 text-red-400'
                                                }`}>
                                                <span className="font-bold flex items-center justify-center gap-2">
                                                    {viewingApp.status === 'accepted' ? <BsCheckCircle /> : <BsXCircle />}
                                                    Application {viewingApp.status === 'accepted' ? 'Accepted' : 'Rejected'}
                                                </span>
                                            </div>
                                        )}
                                    </motion.div>
                                ) : (
                                    <div className="h-full flex items-center justify-center text-white/30 italic border border-white/5 rounded-2xl bg-white/5 min-h-[400px]">
                                        Select an application to view details
                                    </div>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>
                )}
            </div>

            {/* Fullscreen Image Viewer Modal */}
            <AnimatePresence>
                {fullscreenImage && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setFullscreenImage(null)}
                        className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-xl flex items-center justify-center p-4 cursor-zoom-out"
                    >
                        <motion.button
                            initial={{ scale: 0, rotate: -90 }}
                            animate={{ scale: 1, rotate: 0 }}
                            className="absolute top-6 right-6 p-3 bg-premium-gold/20 text-premium-gold rounded-full hover:bg-premium-gold/40 transition-all border border-premium-gold/30"
                            onClick={() => setFullscreenImage(null)}
                        >
                            <BsX className="w-8 h-8" />
                        </motion.button>

                        <motion.div
                            initial={{ scale: 0.8, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.8, y: 20 }}
                            className="relative max-w-4xl max-h-[85vh] rounded-3xl overflow-hidden shadow-[0_0_50px_rgba(212,175,55,0.3)] border border-premium-gold/30"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <img
                                src={fullscreenImage}
                                alt="Profile Fullscreen"
                                className="w-full h-full object-contain bg-premium-wood-dark"
                            />
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default ApplicationsDashboard;
